import { Box, Card, CardActionArea, Chip, IconButton, Typography, Tooltip } from '@mui/material'
import { Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material'
import { Contact } from '../../services/api'
import { tabularNums } from '../../theme'
import type { AssignmentAge } from '../../utils/contactAssignment'
import AssignmentAgeNote from './AssignmentAgeNote'

interface ContactCardProps {
  contact: Contact
  statusLabel: string
  statusColor: 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'
  connectionLabel: string | null
  reachOutLabel: string | null
  onClick: () => void
  /**
   * Staff only. update-contact refuses the priority flag for a portal client,
   * so a client sees the flag read-only rather than a control that would fail.
   */
  onPriorityToggle?: () => void
  /** Staff only (MWA-354): the wait since assignment, for a contact awaiting outreach. */
  assignmentAge?: AssignmentAge | null
}

/**
 * One contact, as a card. This is the phone rendering of a table row, so it
 * carries every field the row does — name, title, company, status, connection,
 * reach-out and priority. A phone showing fewer fields than a desktop is a
 * smaller product, not a responsive one.
 */
export default function ContactCard({
  contact,
  statusLabel,
  statusColor,
  connectionLabel,
  reachOutLabel,
  onClick,
  onPriorityToggle,
  assignmentAge,
}: ContactCardProps) {
  const canToggle = !!onPriorityToggle

  return (
    <Card variant="outlined" sx={{ borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <CardActionArea onClick={onClick} sx={{ p: 1.75, flexGrow: 1, borderRadius: '12px' }}>
          <Typography sx={{ fontSize: '0.9375rem', lineHeight: '1.25rem', fontWeight: 600 }}>
            {contact.fullName}
          </Typography>
          {contact.position && (
            <Typography variant="body2" color="text.secondary">
              {contact.position}
            </Typography>
          )}
          <Typography variant="body2">{contact.company || '—'}</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            <Chip
              label={statusLabel}
              size="small"
              color={statusColor}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
            {connectionLabel && (
              <Chip
                label={connectionLabel}
                size="small"
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              mt: 1.25,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Assigned
            </Typography>
            {reachOutLabel ? (
              <Typography variant="caption" sx={{ ...tabularNums, fontWeight: 500 }}>
                {reachOutLabel}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Not set
              </Typography>
            )}
          </Box>
          {assignmentAge && <AssignmentAgeNote age={assignmentAge} />}
        </CardActionArea>

        {/* Outside the action area: tapping the star must not also open the
            contact, and a button inside a button is not reachable by keyboard. */}
        <Box sx={{ p: 0.75 }}>
          {canToggle ? (
            <Tooltip title={contact.isPriority ? 'Remove priority' : 'Mark as priority'}>
              <IconButton
                aria-label={
                  contact.isPriority
                    ? `Remove priority from ${contact.fullName}`
                    : `Mark ${contact.fullName} as priority`
                }
                onClick={onPriorityToggle}
                sx={{ width: 44, height: 44, color: contact.isPriority ? 'warning.main' : 'action.disabled' }}
              >
                {contact.isPriority ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {contact.isPriority ? (
                <StarIcon titleAccess="Priority contact" sx={{ color: 'warning.main' }} />
              ) : (
                <StarBorderIcon sx={{ color: 'action.disabled' }} />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  )
}
